// An exact port of the API's `owned_cards.canonicalize_card_name` (mtg-api
// app/owned_cards.py), the key the deck-box sync manifest's `zone` is built
// from (plan 12 R3-3). Pinned against EVERY pair of the API-exported golden
// (src/api/__fixtures__/deck_boxes_wire/canonical_names.json).
//
// This is deliberately NOT the app's general name-key helpers (nameKeys,
// frontKey): those lowercase and split faces, but they do not fold
// apostrophes, strip printing metadata or apply NFKD, so they disagree with
// the manifest on names like "Kraum, Ludevic’s Opus". Port quirks the golden
// pins (and this code keeps on purpose): every (...) / [...] group is
// stripped even inside a real name ("Hazmat Suit (Used)" -> "hazmat suit"), a
// bare trailing collector number survives ("Sol Ring (C21) 263" ->
// "sol ring 263"), and "Æ" is not folded to "ae".
//
// Python semantics that JavaScript does not share, reproduced here with
// CPython 3.11's own Unicode 14 data (src/lib/pyUnicode.ts), never the
// browser's tables:
//   - str.strip() / re `\s` use Python's whitespace set (str.isspace), which
//     includes U+001C..U+001F and U+0085 and excludes U+FEFF; JS trim() and
//     `\s` differ on exactly those.
//   - str.lower() is Python's (pyLower), Final_Sigma included.
//   - re `\d` in a str pattern is any Unicode 14 decimal digit (JS `\d` is ASCII).
//   - re `$` (no MULTILINE) also matches just before one trailing newline.
//   - NFKD is Python's (pyNormalize: code points assigned after Unicode 14
//     pass through untouched).
import { PY_DECIMAL_CLASS, PY_SPACE_CLASS, pyLower, pyNormalize } from './pyUnicode'

const WS = PY_SPACE_CLASS
const PY_STRIP = new RegExp(`^[${WS}]+|[${WS}]+$`, 'gu')
const PY_WS_RUN = new RegExp(`[${WS}]+`, 'gu')

/** Python's str.strip() with no argument. */
export function pyStrip(s: string): string {
  return s.replace(PY_STRIP, '')
}

/** Python's `" ".join(s.split())`: whitespace runs collapsed to one space, ends trimmed. */
export function pyCollapseWhitespace(s: string): string {
  return pyStrip(s.replace(PY_WS_RUN, ' '))
}

// C0 / C1 controls except \t \n \r (kept for the whitespace collapse).
// eslint-disable-next-line no-control-regex -- the API strips exactly these control characters
const CONTROL = /[\x00-\x08\x0b\x0c\x0e-\x1f\x7f-\x9f]/g
const APOSTROPHES = /[\u2019\u2018\u02bc`]/g
// re.sub(r'\s*[\[\(][^\]\)]+[\]\)]', '', s)
const BRACKETED = new RegExp(`[${WS}]*[\\[(][^\\])]+[\\])]`, 'gu')
// re.sub(r'\s*\*[A-Za-z]+\*', '', s)
const PROMO_MARKER = new RegExp(`[${WS}]*\\*[A-Za-z]+\\*`, 'gu')
// re.sub(r'\s*[#\-]\s*\d+$', '', s): `$` may sit before one final "\n".
const COLLECTOR_NUMBER = new RegExp(`[${WS}]*[#\\-][${WS}]*[${PY_DECIMAL_CLASS}]+(?=\\n?$)`, 'u')

const NFKD = 'NFKD'

/** `owned_cards.canonicalize_card_name`, step for step. */
export function canonicalizeCardName(name: string): string {
  if (!name) return ''
  let s = pyLower(pyStrip(name))
  s = s.replace(CONTROL, '')
  s = pyNormalize(s, NFKD)
  s = s.replace(APOSTROPHES, "'")
  // eslint-disable-next-line i18next/no-literal-string -- the DFC face separator
  const sep = ' // '
  if (s.includes(sep)) s = pyStrip(s.split(sep)[0])
  s = s.replace(BRACKETED, '')
  s = s.replace(PROMO_MARKER, '')
  s = s.replace(COLLECTOR_NUMBER, '')
  s = s.replace(PY_WS_RUN, ' ')
  return pyStrip(s)
}
